import { useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import FormStepper from '@/components/ui/FormStepper/FormStepper';
import { StepDefinition } from '@/components/ui/FormStepper/types';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { KollelDtoIn, KollelFormPayload } from '@/models/in/KollelDtoIn';
import { createKollelFormSchema, defaultKollelFormValues, KollelFormData } from './schemas/kollelFormSchema';
import { mapFormDataToDto, mapAvrechsToDto } from './utils/mapFormDataToDto';
import KollelDetailsStep from './steps/KollelDetailsStep';
import KollelAvrechsStep from './steps/KollelAvrechsStep';
import KollelScheduleStep from './steps/KollelScheduleStep';
import KollelStipendStep from './steps/KollelStipendStep';
import KollelTermsStep from './steps/KollelTermsStep';

interface NewKollelFormProps {
  fundId?: number;
  onSuccess: () => void;
  /** Returns to the wizard's intro screen from step 1 ("חזרה להתחלה"). */
  onBackToIntro: () => void;
}

const StepFrame = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full max-w-[760px] my-auto px-8 max-md:px-4 py-2">{children}</div>
);

/**
 * The 5-step body of the "כולל חדש" wizard (HAT-1446 phases 3-4). Submission is ONE call carrying
 * the kollel and its avrechim, saved together server-side: as two calls, a rejected avrech left an
 * empty kollel behind and every retry added another (production, 2026-10-21).
 */
const NewKollelForm = ({ fundId, onSuccess, onBackToIntro }: NewKollelFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(() => createKollelFormSchema(t), [t]);
  const methods = useForm<KollelFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultKollelFormValues,
    mode: 'onTouched',
  });

  const { data: existingKollels } = useGetAuthApi<KollelDtoIn[]>(fundId ? `funds/${fundId}/kollels` : '');
  const { data: stipendSettings } = useGetAuthApi<{ maxTotalStipendPerAvrech: number | null }>(
    fundId ? `funds/${fundId}/kollels/stipend-settings` : '',
  );

  const { mutateAsync: createKollel } = usePostAuthApi<KollelDtoIn, KollelFormPayload>(
    fundId ? `funds/${fundId}/kollels` : '',
  );

  const validateStipendStep = () => {
    const max = stipendSettings?.maxTotalStipendPerAvrech;
    if (max == null) return true; // no approved cap yet - nothing to enforce client-side either
    const data = methods.getValues();
    const total = (data.excellenceLimit ?? 0) + (data.examsLimit ?? 0) + (data.punctualityLimit ?? 0);
    if (total > max) {
      setSubmitError(t('kollels.form.stipendTotalWithCap', { total, max }));
      return false;
    }
    setSubmitError(null);
    return true;
  };

  const steps: StepDefinition[] = useMemo(
    () => [
      {
        content: <StepFrame><KollelDetailsStep /></StepFrame>,
        validate: () => methods.trigger(['name']),
      },
      {
        content: (
          <StepFrame>
            <KollelAvrechsStep fundId={fundId} otherKollels={existingKollels ?? []} />
          </StepFrame>
        ),
      },
      {
        content: <StepFrame><KollelScheduleStep /></StepFrame>,
      },
      {
        content: <StepFrame><KollelStipendStep fundId={fundId} /></StepFrame>,
        validate: validateStipendStep,
      },
      {
        content: <StepFrame><KollelTermsStep /></StepFrame>,
        validate: () => methods.trigger(['legalTermsApproved']),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fundId, existingKollels, methods, stipendSettings],
  );

  const handleSubmit = async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const data = methods.getValues();
      await createKollel({ ...mapFormDataToDto(data), avrechs: mapAvrechsToDto(data).avrechs });
      onSuccess();
    } catch (error) {
      console.error('Error creating kollel:', error);
      setSubmitError(t('kollels.wizard.errorCreating'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <FormStepper
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        errorMessage={submitError}
        submitLabel={t('kollels.wizard.step5.submit')}
        backLabel={t('kollels.wizard.backToPrevious')}
        onFirstStepBack={onBackToIntro}
        firstStepBackLabel={t('kollels.wizard.backToStart')}
        backLinkWidthClass="w-[160px]"
      />
    </FormProvider>
  );
};

export default NewKollelForm;
